import { AppConfig } from '@core/domain/AppConfig.ts';
import { EntregaApplicationService } from '@src/modules/entrega-paquetes/application/EntregaApplicationService.ts';
import { SqlJsEntregaAdapter } from '@src/modules/entrega-paquetes/infrastructure/driven/SqlJsEntregaAdapter.ts';
import { BrowserGeolocationAdapter } from '@src/modules/entrega-paquetes/infrastructure/driven/BrowserGeolocationAdapter.ts';
import { BrowserEntregaMapAdapter } from '@src/modules/entrega-paquetes/infrastructure/driven/BrowserEntregaMapAdapter.ts';
import type { GeolocationPort } from '@src/modules/entrega-paquetes/domain/ports/GeolocationPort.ts';
import type { EntregaMapPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaMapPort.ts';
import type { EntregaPdfPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPdfPort.ts';
import { LocalMediaEntregaAdapter } from '@src/modules/entrega-paquetes/infrastructure/driven/LocalMediaEntregaAdapter.ts';
import { BrowserEntregaPdfAdapter } from '@src/modules/entrega-paquetes/infrastructure/driven/BrowserEntregaPdfAdapter.ts';
import { ReposicionApplicationService } from '@src/modules/reposicion-productos/application/ReposicionApplicationService.ts';
import { SqlJsReposicionAdapter } from '@src/modules/reposicion-productos/infrastructure/driven/SqlJsReposicionAdapter.ts';
import { LocalMediaReposicionAdapter } from '@src/modules/reposicion-productos/infrastructure/driven/LocalMediaReposicionAdapter.ts';
import { BrowserReposicionPdfAdapter } from '@src/modules/reposicion-productos/infrastructure/driven/BrowserReposicionPdfAdapter.ts';
import type { ReposicionPdfPort } from '@src/modules/reposicion-productos/domain/ports/ReposicionPdfPort.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/domain/GetSqlJsDatabase.ts';
import { createSyncPushAdapter, preferServiceToken } from '@src/modules/system/infrastructure/driven/apiSyncPushHelpers.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import { authResponsableLabel, authUsuarioPublicId } from '@src/modules/auth/domain/authResponsableLabel.ts';

export type FeatureStackDeps = {
  getDb: GetSqlJsDatabase;
  getToken: () => string | null;
  flushPersist?: () => Promise<void>;
  markDirty?: () => void;
  auth: AuthApplicationPort;
};

export type EntregaStack = {
  service: EntregaApplicationService;
  geolocation: GeolocationPort;
  entregaMap: EntregaMapPort;
  entregaPdf: EntregaPdfPort;
};

export type ReposicionStack = {
  service: ReposicionApplicationService;
  reposicionPdf: ReposicionPdfPort;
};

function offlineServiceArgs(deps: FeatureStackDeps) {
  return [deps.flushPersist, deps.getDb, preferServiceToken(deps.getToken), deps.markDirty] as const;
}

async function resolvePushActor(auth: AuthApplicationPort) {
  const user = await auth.getCurrentUser();
  const usuarioPublicId = authUsuarioPublicId(user);
  if (!usuarioPublicId) return null;
  return { usuarioPublicId, responsableLabel: authResponsableLabel(user) };
}

export function createEntregaStack(deps: FeatureStackDeps): EntregaStack {
  const adapter = new SqlJsEntregaAdapter(deps.getDb);
  const [flushPersist, getDb, getToken, markDirty] = offlineServiceArgs(deps);
  const entregaMap = new BrowserEntregaMapAdapter();
  const entregaPdf = new BrowserEntregaPdfAdapter();
  return {
    service: new EntregaApplicationService(
      adapter,
      adapter,
      adapter,
      new LocalMediaEntregaAdapter(),
      createSyncPushAdapter('entregas', preferServiceToken(deps.getToken), AppConfig.getCatalogPushUrl()),
      flushPersist,
      getDb,
      getToken,
      markDirty,
      {
        resolvePushActor: () => resolvePushActor(deps.auth),
      },
    ),
    geolocation: new BrowserGeolocationAdapter(),
    entregaMap,
    entregaPdf,
  };
}

export function createReposicionStack(deps: FeatureStackDeps): ReposicionStack {
  const adapter = new SqlJsReposicionAdapter(deps.getDb);
  const [flushPersist, getDb, getToken, markDirty] = offlineServiceArgs(deps);
  return {
    service: new ReposicionApplicationService(
      adapter,
      adapter,
      new LocalMediaReposicionAdapter(),
      createSyncPushAdapter('reposiciones', preferServiceToken(deps.getToken), AppConfig.getCatalogPushUrl()),
      flushPersist,
      getDb,
      getToken,
      markDirty,
      async () => {
        const actor = await resolvePushActor(deps.auth);
        return actor ? { usuarioPublicId: actor.usuarioPublicId } : null;
      },
    ),
    reposicionPdf: new BrowserReposicionPdfAdapter(),
  };
}
